/**
 * quiz-loader.ts — Loads quiz definitions from embedded JSON files.
 *
 * Quiz definitions are loaded eagerly via Vite's import.meta.glob and cached
 * by their canonical ID (e.g. "01-session-script-intro-session/debrief-quiz"). This mirrors the pattern
 * used by the dialogue loader in domains/dialogue.ts.
 */

// ── Types ──────────────────────────────────────────────────────────────────

interface QuizOption {
  /** Short identifier for this option sent to the server (e.g. "a", "b"). */
  id: string;
  /** i18n translation key for the option text. */
  textKey: string;
}

export interface QuizQuestion {
  /** Stable question identifier (e.g. "q1"). */
  id: string;
  /** "multiple_choice" or "free_text". */
  type: 'multiple_choice' | 'free_text';
  /** i18n translation key for the question text. */
  textKey: string;
  /** Options list — only present for multiple_choice questions. */
  options?: QuizOption[];
  /**
   * ID of the correct option for multiple_choice questions.
   * null means this is an opinion question with no wrong answer.
   */
  correctOptionId: string | null;
  /** Max number of answer attempts (only meaningful when correctOptionId is set). */
  maxAttempts?: number;
  /** Minimum character count for free_text answers. */
  minLength?: number;
  /** Per-question time limit in seconds. */
  timeLimitSec: number;
}

/**
 * Whether a question has a right answer the server grades: a multiple-choice
 * question with a correct option (phase-quiz.go, HasCorrectAnswer). An opinion
 * or a free answer is taken as given, with no verdict.
 */
export function isGraded(question: QuizQuestion): boolean {
  return question.type === 'multiple_choice' && !!question.correctOptionId;
}

/**
 * How many tries a question gives: its maxAttempts, never fewer than one, as
 * phase-quiz.go reads it. Only a graded question is ever tried again.
 */
export function triesFor(question: QuizQuestion): number {
  return Math.max(1, question.maxAttempts ?? 1);
}

/** A free answer's length as the server counts it: its characters, trimmed as the client sends it. */
export function answerLength(text: string): number {
  return [...text.trim()].length;
}

/** Whether a free answer is long enough for the server to take it (phase-quiz.go, minLength). */
export function longEnough(question: QuizQuestion, text: string): boolean {
  return !question.minLength || answerLength(text) >= question.minLength;
}

interface QuizDefinition {
  /** Canonical quiz ID (e.g. "01-session-script-intro-session/debrief-quiz"). */
  id: string;
  /** Ordered list of questions. */
  questions: QuizQuestion[];
}

// ── Cache and loader ───────────────────────────────────────────────────────

const quizModules = import.meta.glob('/src/data/quizzes/**/*.json', { eager: true });

const quizCache = new Map<string, QuizDefinition>();

function buildCache() {
  if (quizCache.size > 0) return;
  for (const raw of Object.values(quizModules)) {
    const mod = raw as { default?: QuizDefinition } & QuizDefinition;
    const def: QuizDefinition = mod.default ?? mod;
    quizCache.set(def.id, def);
  }
}

/**
 * Returns the QuizDefinition for the given quiz ID (e.g. "01-session-script-intro-session/debrief-quiz").
 * Returns undefined if no matching definition is found.
 */
export function loadQuiz(id: string): QuizDefinition | undefined {
  buildCache();
  return quizCache.get(id);
}
